import { createContext, type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { Animated } from 'react-native';

type PhaseAnimationValue = {
  phase: number;
  fadeAnim: Animated.Value;
};

const PhaseAnimationContext = createContext<PhaseAnimationValue | null>(null);

export function usePhaseAnimation() {
  const ctx = useContext(PhaseAnimationContext);
  if (!ctx) throw new Error('usePhaseAnimation must be used inside PhaseAnimationProvider');
  return ctx;
}

interface PhaseAnimationProviderProps extends PropsWithChildren {
  phaseCount: number;
  interval: number;
  fadeDuration: number;
}

export function PhaseAnimationProvider({ children, phaseCount, interval, fadeDuration }: PhaseAnimationProviderProps) {
  const [phase, setPhase] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const id = setInterval(() => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: fadeDuration,
        useNativeDriver: true,
      }).start(() => {
        setPhase((p) => (p + 1) % phaseCount);
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: fadeDuration,
          useNativeDriver: true,
        }).start();
      });
    }, interval);
    return () => clearInterval(id);
  }, [fadeAnim, phaseCount, interval, fadeDuration]);

  return <PhaseAnimationContext.Provider value={{ phase, fadeAnim }}>{children}</PhaseAnimationContext.Provider>;
}
